import React, { useEffect, useState } from 'react';

// Slowly reveals `text` one character at a time, holds it, then clears and
// loops — a nod to the achingly slow letter-by-letter text it references.
// Width is reserved by a hidden sizer copy so surrounding text never reflows,
// and prefers-reduced-motion gets the full text with no animation.
function Typewriter({ text, className = '', charDelay = 250, holdDelay = 2200 }) {
  const [prefersReduced] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const [count, setCount] = useState(0);
  const [phase, setPhase] = useState('typing'); // 'typing' | 'holding'

  useEffect(() => {
    if (prefersReduced) return undefined;

    let timer;
    if (phase === 'typing') {
      if (count < text.length) {
        timer = setTimeout(() => setCount((c) => c + 1), charDelay);
      } else {
        timer = setTimeout(() => setPhase('holding'), holdDelay);
      }
    } else {
      // Held the full phrase; clear and start typing again.
      timer = setTimeout(() => {
        setCount(0);
        setPhase('typing');
      }, holdDelay);
    }

    return () => clearTimeout(timer);
  }, [count, phase, text, charDelay, holdDelay, prefersReduced]);

  if (prefersReduced) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className} aria-label={text}>
      {/* invisible full copy reserves the final width (no reflow while typing) */}
      <span className="typewriter__sizer" aria-hidden="true">
        {text}
      </span>
      <span className="typewriter__reveal" aria-hidden="true">
        {text.slice(0, count)}
        <span className="typewriter__caret" />
      </span>
    </span>
  );
}

export default Typewriter;
